import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "@components/Modal";
import { useModal } from "@components/Modal/ModalProvider";
import styles from "../styles/SegmentManager.module.scss";
import {
  getManualCanApplyModification,
  getManualDirty,
  getManualMode,
  onManualCanApplyModificationChanged,
  onManualDirtyChanged,
  onManualModeChanged,
  setManualCanApplyModification,
  setManualDirty,
  setManualMode,
} from "@features/analysis/rendering/Panels/manual";

const discardColor = getComputedStyle(document.documentElement)
  .getPropertyValue("--background-error-filled")
  .trim();
const applyColor = getComputedStyle(document.documentElement)
  .getPropertyValue("--background-primary-filled")
  .trim();

export function useSegmentManager(){
  const [mode, setModeState] = useState(false);
  const [dirty, setDirtyState] = useState(false);
  const [canApplyModification, setCanApplyModificationState] = useState(
    getManualCanApplyModification,
  );

  const { openModal, closeModal } = useModal();
  const modalIdRef = useRef<string | null>(null);
  const setMode = useCallback((value: boolean) => {
    setManualMode(value);
    setModeState(value);
  }, []);

  const setDirty = useCallback((value: boolean) => {
    setManualDirty(value);
    setDirtyState(value);
  }, []);

  const setCanApplyModification = useCallback((value: boolean) => {
    setManualCanApplyModification(value);
    setCanApplyModificationState(value);
  }, []);

  useEffect(() => {
    let alive = true;
    let modeVersion=0;

    const applyMode=(value:boolean)=>{
      if(!alive) return;
      modeVersion+=1;
      setModeState(value);
    }
    const offMode=onManualModeChanged(applyMode);

    const syncVerison=modeVersion;
    getManualMode().then((value)=>{
      if(alive&&syncVerison===modeVersion){
        setModeState(value);
      }
    });

    getManualDirty().then((value) => {
      if (alive) setDirtyState(value);
    });


    const offDirty = onManualDirtyChanged((value) => {
      setDirtyState(value);
    });

    const offCanApplyModification = onManualCanApplyModificationChanged(
      (value) => {
        setCanApplyModificationState(value);
      },
    );

    return () => {
      alive = false;
      offMode();
      offDirty();
      offCanApplyModification();
    };
  }, []);

  const closeWithFlagOff = useCallback(() => {
    if (modalIdRef.current) closeModal(modalIdRef.current)
    modalIdRef.current = null
  },[modalIdRef]);

  const open = useCallback((params: {
      message:string,
      cancelCallback?:any,
      discardCallback?:any,
      applyCallback?:any,
    }) => {
    if(modalIdRef.current) return;
    const {message,cancelCallback,discardCallback,applyCallback} = params;
    modalIdRef.current = openModal(
      <Modal
        type='page'
        close={() => closeWithFlagOff()}
        description={
        <div className={styles['modal']}>
          <div className={styles['header']}>
            <span>{message}</span>
          </div>
          <div className={styles['modal-action']}>
            <div
            className={`${styles['button']} ${styles['cancel']}`}
            onClick={()=>{
              cancelCallback?.();
              closeWithFlagOff();}
            }>
              <span>{'Cancel'}</span>
            </div>
            {discardCallback && <div
              className={styles['button']}
              onClick={()=>{
                discardCallback();
                closeWithFlagOff();
              }}
              style={{backgroundColor:discardColor}}>
              <span>{'Discard'}</span>
            </div>}
            {applyCallback && <div
              className={styles['button']}
              onClick={()=>{
                applyCallback();
                closeWithFlagOff();
              }}
              style={{backgroundColor:applyColor}}>
              <span>{'Apply'}</span>
            </div>}
            {/* {actions.map((type,index)=>(
            ))} */}
          </div>
        </div>
      }
      />,
      false,
      () => closeWithFlagOff(),
      true
    );
  }, [modalIdRef, openModal, closeWithFlagOff])

  useEffect(() => {
    if (modalIdRef.current) {
      closeWithFlagOff();
    }
  }, []);

  return {
    mode,
    dirty,
    canApplyModification,
    setMode,
    setDirty,
    setCanApplyModification,
    open,
    closeModal,
  };
}
